if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.buttonPrev = this.querySelector("[data-modal-prev]");
        this.buttonNext = this.querySelector("[data-modal-next]");

        // The base ModalDialog class closes the whole modal on any
        // "pointerup" inside it (click-to-dismiss on the backdrop). Stop
        // that event from reaching this element for our own interactive
        // controls, otherwise every arrow/thumbnail click also closes
        // the modal instead of just switching media.
        [this.buttonPrev, this.buttonNext, ...this.querySelectorAll(".product-media-modal-thumbnail")]
          .filter(Boolean)
          .forEach((element) => {
            element.addEventListener("pointerup", (event) => {
              event.stopPropagation();
            });
          });

        this.buttonPrev?.addEventListener("click", () => {
          this.showAdjacentMedia(-1);
        });
        this.buttonNext?.addEventListener("click", () => {
          this.showAdjacentMedia(1);
        });

        this.querySelectorAll(".product-media-modal-thumbnail").forEach((thumbnail) => {
          thumbnail.addEventListener("click", () => {
            const media = this.querySelector(
              `.product-media-modal-content > [data-media-id="${thumbnail.dataset.mediaId}"]`,
            );
            this.activateMedia(media);
          });
        });

        this.touchStartX = null;
        this.touchStartY = null;

        const content = this.querySelector(".product-media-modal-content");
        content?.addEventListener("touchstart", (event) => {
          this.touchStartX = event.touches[0].clientX;
          this.touchStartY = event.touches[0].clientY;
        }, { passive: true });

        content?.addEventListener("touchend", (event) => {
          if (this.touchStartX === null) {
            return;
          }

          const deltaX = event.changedTouches[0].clientX - this.touchStartX;
          const deltaY = event.changedTouches[0].clientY - this.touchStartY;
          this.touchStartX = null;
          this.touchStartY = null;

          const SWIPE_THRESHOLD = 40;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) {
            return;
          }

          this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
        });
      }

      hide() {
        super.hide();
        publish(PUB_SUB_EVENTS.productModalClose);
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        publish(PUB_SUB_EVENTS.productModalOpen);
      }

      getMediaItems() {
        return [...this.querySelectorAll(".product-media-modal-content > [data-media-id]")];
      }

      activateMedia(activeMedia) {
        if (!activeMedia) {
          return;
        }

        this.getMediaItems().forEach((element) => {
          element.classList.remove("is-active");
        });

        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add("is-active");
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }

        this.querySelectorAll(".product-media-modal-thumbnail").forEach((thumbnail) => {
          thumbnail.classList.toggle(
            "is-active",
            thumbnail.dataset.mediaId === activeMedia.dataset.mediaId,
          );
        });

        const activeThumbnail = this.querySelector(
          `.product-media-modal-thumbnail[data-media-id="${activeMedia.dataset.mediaId}"]`,
        );
        activeThumbnail?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });

        this.preloadAdjacentMedia(activeMedia);
      }

      // Eagerly load the previous/next slide's image so stepping through
      // the gallery feels instant instead of waiting on a fresh fetch
      // (all modal images start as loading="lazy" since the modal itself
      // is hidden until opened).
      preloadAdjacentMedia(activeMedia) {
        const items = this.getMediaItems();
        const index = items.indexOf(activeMedia);

        if (index === -1 || items.length < 2) {
          return;
        }

        const prevIndex = (index - 1 + items.length) % items.length;
        const nextIndex = (index + 1) % items.length;

        [items[prevIndex], items[nextIndex]].forEach((item) => {
          const img = item.nodeName === "IMG" ? item : item.querySelector("img");
          if (img && img.loading === "lazy") {
            img.loading = "eager";
          }
        });
      }

      showActiveMedia() {
        const activeMedia = this.querySelector(
          `.product-media-modal-content > [data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        this.activateMedia(activeMedia);
      }

      showAdjacentMedia(direction) {
        const items = this.getMediaItems();

        if (!items.length) {
          return;
        }

        const currentIndex = items.findIndex((element) => element.classList.contains("is-active"));
        const nextIndex = (currentIndex + direction + items.length) % items.length;

        this.activateMedia(items[nextIndex]);
      }
    },
  );
}
